import {PrimeNgImportsModule} from '../../../../../sdk/src/prime-ng-imports';
import {IBasePageComponent} from '../../../../../sdk/src/components/ibase-page/ibase-page.component';
import {CommonModule} from '@angular/common';
import {Component, inject, ViewChild} from '@angular/core';
import {StringUtils} from '../../../../../sdk/src/utils/string.util';
import {
    ECpmCLocationTypeApiEnum,
    ECpmCLocationVectorTypeApiEnum,
    RECpmCLocationTypeApiEnum
} from '../../../../../gen/cpm-c-api-ts/src/enums/c-customer/cpmc-location.api-enums';
import {LocationCRUDComponent} from '../../location-crud/location-crud.component';
import {CpmCLocationApiModel} from '../../../../../gen/cpm-c-api-ts/src/models/c-customer/cpmc-location.api-model';
import {TreeNode} from 'primeng/api';
import {CpmCLocationTreeApiView} from '../../../../../gen/cpm-c-api-ts/src/views/c-customer/cpmc-location-tree.api-view';
import {LocationUtils} from '../../../utils/location.util';
import {CpmCLocationApiUtils} from '../../../../../gen/cpm-c-api-ts/src/utils/c-customer/cpmc-location.api-utils';
import {LocationHierarchyTreeComponent} from '../../location-hierarchy-tree/location-hierarchy-tree.component';
import {TreeUtils} from '../../../../../sdk/src/utils/tree.utils';
import {IBaseCRUModalResultBase} from '../../../../../sdk/src/components/ibase-cru-modal/ibase-cru-modal.component';
import {NewEditLocationModalResult} from '../../../modals/new-edit-location-modal/new-edit-location-modal.component';
import {EDialogOpenMode} from '../../../../../sdk/src/enums/dialog-open-mode.enum';
import {firstValueFrom} from 'rxjs';
import {CpmCLocationApiService} from '../../../../../gen/cpm-c-api-ts/src/services/c-customer/cpmc-location.api-service';
import {CustomerUrlBuilderUtil} from '../../../utils/customer-url-builder.util';
import {CustomerLayoutComponent} from '../../../section-customer/customer-layout.component';

@Component({
    selector: 'app-organization-hierarchy',
    templateUrl: './organization-hierarchy.component.html',
    styleUrl: './organization-hierarchy.component.scss',
    imports: [CommonModule, PrimeNgImportsModule, LocationHierarchyTreeComponent, LocationCRUDComponent]
})
export class OrganizationHierarchyComponent extends IBasePageComponent {
    protected readonly LocationUtils = LocationUtils;
    protected readonly CPMLocationApiUtils = CpmCLocationApiUtils;
    protected readonly ECPMLocationVectorTypeApiEnum = ECpmCLocationVectorTypeApiEnum;

    // ===============================================================
    // ===============================================================

    private readonly customerLayout = inject(CustomerLayoutComponent);

    // ===============================================================
    // ===============================================================

    @ViewChild(LocationCRUDComponent)
    protected locationCRUD?: LocationCRUDComponent;

    protected activeLocation?: CpmCLocationApiModel;
    protected activeLocationTreeNode?: TreeNode<CpmCLocationTreeApiView>;
    protected typeOfLocationOptions = Object.keys(ECpmCLocationTypeApiEnum).map((key) => ({
        label: StringUtils.toHumanFormatedStr(key),
        value: RECpmCLocationTypeApiEnum[key]
    }));
    protected waitInitialLocationsLoading = false;

    // ===============================================================
    // ===============================================================

    /**
     * Constructor
     */
    constructor() {
        super();
    }

    // ===============================================================
    // ===============================================================

    /**
     * On ready (Error handling and refresh is done in the parent)
     */

    protected override async onReady(): Promise<void> {
        this.waitInitialLocationsLoading = true;

        await this.globalsService.sessionService.getRouteContextIsReadyPromise();

        LocationHierarchyTreeComponent.activeUUIDPath = '';
        LocationHierarchyTreeComponent.onLocationsLoaded = this.onLocationsLoaded.bind(this);
        LocationHierarchyTreeComponent.onLocationClick = this.onLocationTreeNodeSelected.bind(this);

        if (this.globalsService.sessionService.getLocation()) this.waitInitialLocationsLoading = true;
        else this.waitInitialLocationsLoading = false;
    }
    /**
     * On destroy (Error handling and refresh is done in the parent)
     */

    protected override async onDestroy(): Promise<void> {
        LocationHierarchyTreeComponent.onLocationsLoaded = undefined;
        LocationHierarchyTreeComponent.onLocationClick = undefined;
    }

    // ===============================================================
    // ===============================================================

    /**
     * On parent access
     * @param parent Target parent
     */
    protected async onParentAccess(parent: CpmCLocationApiModel) {
        let treeNode = TreeUtils.findTreeNodeByKey(LocationHierarchyTreeComponent.treeItemRoot.children!, parent.uuid!);

        // If tree node is not found, attempt a refresh and give it a second chance
        if (!treeNode) {
            await LocationHierarchyTreeComponent.instance?.refresh();
            treeNode = TreeUtils.findTreeNodeByKey(LocationHierarchyTreeComponent.treeItemRoot.children!, parent.uuid!);
        }

        this.onLocationTreeNodeSelected(treeNode?.data ?? parent, treeNode);
    }
    /**
     * On children access
     * @param children Target children
     */
    protected async onChildrenAccess(children: CpmCLocationApiModel) {
        let treeNode = TreeUtils.findTreeNodeByKey(
            LocationHierarchyTreeComponent.treeItemRoot.children!,
            children.uuid!
        );

        // If tree node is not found, attempt a refresh and give it a second chance
        if (!treeNode) {
            await LocationHierarchyTreeComponent.instance?.refresh();
            treeNode = TreeUtils.findTreeNodeByKey(
                LocationHierarchyTreeComponent.treeItemRoot.children!,
                children.uuid!
            );
        }

        this.onLocationTreeNodeSelected(treeNode?.data ?? children, treeNode);
    }

    // ===============================================================
    // ===============================================================

    /**
     * On locations loaded
     */
    private async onLocationsLoaded() {
        if (!this.waitInitialLocationsLoading) return;

        const location = this.globalsService.sessionService.getLocation();
        if (location) {
            const treeNode = TreeUtils.findTreeNodeByKey(
                LocationHierarchyTreeComponent.treeItemRoot.children!,
                location.uuid!
            );
            if (treeNode) await this.onLocationTreeNodeSelected(treeNode.data!, treeNode);
        }
    }
    /**
     * On location tree node selected
     * @param location Selected location
     * @param locationTreeNode Selected location tree node
     */
    private async onLocationTreeNodeSelected(
        locationTree: CpmCLocationTreeApiView,
        locationTreeNode?: TreeNode<CpmCLocationTreeApiView>
    ): Promise<void> {
        this.showLoading = true;

        let activeUUIDPath = '';
        let current = locationTreeNode;
        while (current) {
            if (activeUUIDPath.length != 0) activeUUIDPath = '/' + activeUUIDPath;

            activeUUIDPath = current.data?.uuid + activeUUIDPath;
            current = current.parent;

            if (current == LocationHierarchyTreeComponent.treeItemRoot) break;
        }

        this.activeLocationTreeNode = locationTreeNode;
        LocationHierarchyTreeComponent.activeUUIDPath = activeUUIDPath;
        LocationHierarchyTreeComponent.instance?.autoExpandActiveLocations();

        // Update browser URL
        const customer = this.globalsService.sessionService.getCustomer();
        const organization = this.globalsService.sessionService.getOrganization();
        this.globalsService.router.navigate(
            [
                CustomerUrlBuilderUtil.customerOrganizationHierarchyLocation(
                    customer!.uuid,
                    organization!.uuid,
                    locationTree.uuid
                )
            ],
            {
                replaceUrl: true,
                skipLocationChange: false
            }
        );

        // Update breadcrumb
        this.globalsService.sessionService.setLocation({
            uuid: locationTree.uuid,
            organization_uuid: organization!.uuid,
            name: locationTree.name
        } as CpmCLocationApiModel);
        this.customerLayout.header?.refreshBreadcrumbs();

        await this.parallelSequencedAsyncCritical([
            {
                m: 'Loading location...',
                f: async () => {
                    this.activeLocation = await firstValueFrom(
                        CpmCLocationApiService.getLocationByUUID(locationTree.uuid!)
                    );

                    // Update breadcrumb
                    this.globalsService.sessionService.setLocation(this.activeLocation);
                    this.customerLayout.header?.refreshBreadcrumbs();
                }
            }
        ]);

        this.showLoading = false;

        this.waitInitialLocationsLoading = false;
    }

    // ===============================================================
    // ===============================================================

    /**
     * On modal result
     * @param modalResult result of the modal
     */

    protected onCRUDModalResult(modalResult: IBaseCRUModalResultBase<NewEditLocationModalResult>) {
        LocationHierarchyTreeComponent?.instance?.refresh();

        if (
            modalResult?.dialogOpenMode === EDialogOpenMode.EDIT &&
            modalResult?.extended?.item?.uuid === this.activeLocation?.uuid
        )
            this.activeLocation = modalResult.extended?.item;
    }
    /**
     * On delete result
     * @param locationModel Delete location model
     */
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    protected async onCRUDDeleteResult(locationModel: CpmCLocationApiModel) {
        if (this.activeLocationTreeNode?.parent) {
            await LocationHierarchyTreeComponent.instance?.selectLocation(
                this.activeLocationTreeNode.parent.data!,
                this.activeLocationTreeNode.parent
            );
        } else {
            LocationHierarchyTreeComponent.instance?.deselectLocation();
            this.activeLocationTreeNode = undefined;
            this.activeLocation = undefined;
        }

        LocationHierarchyTreeComponent?.instance?.refresh();
    }
}
